import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";

import {
  getWorkflowStatuses,
  createWorkflowStatus,
  updateWorkflowStatus,
  reorderWorkflowStatuses,
  deleteWorkflowStatus,
} from "../../services/taskWorkflowStatusService";

const DEFAULT_COLOR = "#94a3b8";

// Super-admin only settings for a project's custom board workflow (columns).
function WorkflowSettingsDialog({ open, projectId, onClose, onChanged }) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteConflict, setDeleteConflict] = useState(null); // { status, reassignTo }

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getWorkflowStatuses(projectId);
      setStatuses(data);
    } catch (err) {
      console.error("Workflow Statuses Load Error:", err.response?.data || err);
      setError("Failed to load workflow statuses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && projectId) {
      load();
      setNewName("");
      setNewColor(DEFAULT_COLOR);
      setDeleteConflict(null);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const notifyChanged = () => {
    if (onChanged) onChanged();
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      setError("Status name is required");
      return;
    }

    try {
      setIsAdding(true);
      setError("");
      await createWorkflowStatus(projectId, { status_name: newName.trim(), color: newColor, is_done: false });
      setNewName("");
      setNewColor(DEFAULT_COLOR);
      await load();
      notifyChanged();
    } catch (err) {
      console.error("Add Workflow Status Error:", err.response?.data || err);
      setError(err?.response?.data?.detail || "Failed to add status");
    } finally {
      setIsAdding(false);
    }
  };

  const handleRename = async (status, name) => {
    if (!name.trim() || name.trim() === status.status_name) {
      return;
    }

    try {
      setSavingId(status.status_id);
      await updateWorkflowStatus(projectId, status.status_id, { status_name: name.trim() });
      await load();
      notifyChanged();
    } catch (err) {
      console.error("Rename Workflow Status Error:", err.response?.data || err);
      setError(err?.response?.data?.detail || "Failed to rename status");
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const handleColorChange = async (status, color) => {
    try {
      await updateWorkflowStatus(projectId, status.status_id, { color });
      setStatuses((prev) => prev.map((s) => (s.status_id === status.status_id ? { ...s, color } : s)));
      notifyChanged();
    } catch (err) {
      console.error("Update Workflow Status Color Error:", err.response?.data || err);
      setError(err?.response?.data?.detail || "Failed to update color");
    }
  };

  const handleIsDoneToggle = async (status, isDone) => {
    try {
      setSavingId(status.status_id);
      await updateWorkflowStatus(projectId, status.status_id, { is_done: isDone });
      await load();
      notifyChanged();
    } catch (err) {
      console.error("Update Workflow Status Error:", err.response?.data || err);
      setError(err?.response?.data?.detail || "Failed to update status");
    } finally {
      setSavingId(null);
    }
  };

  const handleMove = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= statuses.length) {
      return;
    }

    const reordered = [...statuses];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    setStatuses(reordered);

    try {
      await reorderWorkflowStatuses(projectId, reordered.map((s) => s.status_id));
      notifyChanged();
    } catch (err) {
      console.error("Reorder Workflow Statuses Error:", err.response?.data || err);
      setError(err?.response?.data?.detail || "Failed to reorder statuses");
      await load();
    }
  };

  const handleDeleteClick = async (status) => {
    try {
      setSavingId(status.status_id);
      setError("");
      await deleteWorkflowStatus(projectId, status.status_id);
      await load();
      notifyChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      // Backend asks for a reassignment target when tasks still use this status.
      if (err?.response?.status === 400 && /reassign_to_status_id/i.test(detail)) {
        setDeleteConflict({ status, reassignTo: "" });
      } else {
        console.error("Delete Workflow Status Error:", err.response?.data || err);
        setError(detail || "Failed to delete status");
      }
    } finally {
      setSavingId(null);
    }
  };

  const handleConfirmReassignDelete = async () => {
    if (!deleteConflict?.reassignTo) {
      return;
    }

    try {
      setSavingId(deleteConflict.status.status_id);
      await deleteWorkflowStatus(projectId, deleteConflict.status.status_id, deleteConflict.reassignTo);
      setDeleteConflict(null);
      await load();
      notifyChanged();
    } catch (err) {
      console.error("Delete Workflow Status Error:", err.response?.data || err);
      setError(err?.response?.data?.detail || "Failed to delete status");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Board Workflow
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
          Customize this project's task board columns. Statuses flagged "Done" count towards
          subtask progress and sprint velocity.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {statuses.map((status, index) => (
              <Box key={status.status_id}>
                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="color"
                    value={status.color || DEFAULT_COLOR}
                    onChange={(e) => handleColorChange(status, e.target.value)}
                    style={{ width: 28, height: 28, border: "none", padding: 0, background: "none", cursor: "pointer" }}
                  />

                  <TextField
                    size="small"
                    defaultValue={status.status_name}
                    onBlur={(e) => handleRename(status, e.target.value)}
                    disabled={savingId === status.status_id}
                    sx={{ flex: 1 }}
                  />

                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(status.is_done)}
                        onChange={(e) => handleIsDoneToggle(status, e.target.checked)}
                        disabled={savingId === status.status_id}
                      />
                    }
                    label={<Typography sx={{ fontSize: 12 }}>Done</Typography>}
                  />

                  <Tooltip title="Move up">
                    <span>
                      <IconButton size="small" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton size="small" onClick={() => handleMove(index, 1)} disabled={index === statuses.length - 1}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(status)}
                        disabled={savingId === status.status_id || statuses.length <= 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {deleteConflict?.status.status_id === status.status_id && (
                  <Box sx={{ ml: 5, mt: 1, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      Tasks in "{status.status_name}" - move them to:
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={deleteConflict.reassignTo}
                      onChange={(e) => setDeleteConflict((prev) => ({ ...prev, reassignTo: e.target.value }))}
                      sx={{ minWidth: 160 }}
                    >
                      {statuses.filter((s) => s.status_id !== status.status_id).map((s) => (
                        <MenuItem key={s.status_id} value={s.status_id}>{s.status_name}</MenuItem>
                      ))}
                    </TextField>
                    <Button size="small" onClick={handleConfirmReassignDelete} disabled={!deleteConflict.reassignTo}>
                      Move & Delete
                    </Button>
                    <Button size="small" color="inherit" onClick={() => setDeleteConflict(null)}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />

            <Box display="flex" alignItems="center" gap={1}>
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                style={{ width: 28, height: 28, border: "none", padding: 0, background: "none", cursor: "pointer" }}
              />
              <TextField
                size="small"
                placeholder="New status name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                sx={{ flex: 1 }}
              />
              <Button
                size="small"
                startIcon={isAdding ? <CircularProgress size={14} /> : <AddIcon fontSize="small" />}
                onClick={handleAdd}
                disabled={isAdding}
              >
                Add
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default WorkflowSettingsDialog;
